/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { breakpoints } from '@coursera/cds-core';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import AnnualUpgradeConfirmationModal from 'bundles/coursera-plus-common/components/monthlyToAnnualUpgrade/AnnualUpgradeConfirmationModal';
import AnnualUpgradeModal from 'bundles/coursera-plus-common/components/monthlyToAnnualUpgrade/AnnualUpgradeModal';
import StartAnnualUpgradeModal from 'bundles/coursera-plus-common/components/monthlyToAnnualUpgrade/StartAnnualUpgradeModal';
import { usePlusMonthlyToAnnualUpgradeData } from 'bundles/coursera-plus-common/utils/plusMonthlyToAnnualUpgradeUtils';
import { getEligibleCourseraPlusMonthlySubscription } from 'bundles/coursera-plus-common/utils/subscriptionUtils';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import { useGetLoggedInUserSubscriptions } from 'bundles/subscriptions-common/hooks/useGetLoggedInUserSubscriptions';

import _t from 'i18n!nls/coursera-plus-common';

type Props = {
  isEligibleForPlusMonthlyToAnnualUpgrade: boolean;
};

const styles = {
  buttonContainer: () => css`
    display: inline-block;

    @media (max-width: 1023.95px) {
      width: 100% !important;
      max-width: 100%;
    }
  `,
  button: css`
    width: 100%;
    max-width: 100%;
    padding: var(--cds-spacing-150) var(--cds-spacing-300);

    ${breakpoints.up('md')} {
      min-width: 275px;
      max-width: 275px;
    }
  `,
};

const CourseraPlusMonthlyToAnnualUpgradeContainer = ({ isEligibleForPlusMonthlyToAnnualUpgrade }: Props) => {
  const [showStartAnnualUpgradeModal, setShowStartAnnualUpgradeModal] = useState(false);
  const [showAnnualUpgradeModal, setShowAnnualUpgradeModal] = useState(false);
  const [hasUpgraded, setHasUpgraded] = useState(false);
  const { subscriptions } = useGetLoggedInUserSubscriptions({ skip: false });
  const subscription = getEligibleCourseraPlusMonthlySubscription(subscriptions || []);
  const { monthlySavings, courseraPlusAnnualPrice } = usePlusMonthlyToAnnualUpgradeData(
    (subscription || {}) as SubscriptionsV1,
    'XDP'
  );
  const hideUpgradeContainer =
    !isEligibleForPlusMonthlyToAnnualUpgrade || !subscription || !courseraPlusAnnualPrice || !monthlySavings;

  if (hideUpgradeContainer) {
    return null;
  }

  return (
    <div css={styles.buttonContainer}>
      <StartAnnualUpgradeModal
        isOpen={showStartAnnualUpgradeModal}
        onClose={() => {
          setShowStartAnnualUpgradeModal(false);
        }}
        openAnnualUpgradeModal={() => {
          setShowStartAnnualUpgradeModal(false);
          setShowAnnualUpgradeModal(true);
        }}
        monthlySavings={monthlySavings}
        subscription={subscription}
        courseraPlusAnnualPrice={courseraPlusAnnualPrice}
      />
      <AnnualUpgradeModal
        subscription={subscription}
        courseraPlusAnnualPrice={courseraPlusAnnualPrice}
        onClose={() => {
          setShowAnnualUpgradeModal(false);
        }}
        onUpgradeSuccess={() => {
          setShowAnnualUpgradeModal(false);
          setHasUpgraded(true);
        }}
        isOpen={showAnnualUpgradeModal}
      />
      {hasUpgraded && <AnnualUpgradeConfirmationModal />}
      {!hasUpgraded && (
        <TrackedCdsButton
          trackingName="xdp_cplus_monthly_to_annual_button"
          onClick={() => setShowStartAnnualUpgradeModal(true)}
          variant="secondary"
          size="medium"
          fullWidth
          data-testid="xdp-cplus-monthly-to-annual-button"
          css={styles.button}
        >
          <div>
            {_t('Switch subscription')}
            <div className="dual-cta-subtext">
              <FormattedMessage
                message={_t('Save {monthlySavings}/mo with Coursera Plus annual')}
                monthlySavings={monthlySavings}
                tagName="div"
              />
            </div>
          </div>
        </TrackedCdsButton>
      )}
    </div>
  );
};

export default CourseraPlusMonthlyToAnnualUpgradeContainer;
